import {
    Invoice,
    ProviderClaimPayment,
    ProviderClaimPaymentItem,
    ProviderGuarantor,
    ProviderPaymentPlan,
    renderAmount,
    renderDate,
    Transaction,
} from '@enter/core';
import { mdiPlus } from '@mdi/js';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { DialogContext } from 'services';
import { RootStore } from 'stores';
import * as S from './billing-timeline-table.styles';
import { CreatePlanDialog } from './create-plan/create-plan-dialog';

interface IInvoiceDetailProps {
    guarantor?: ProviderGuarantor;
    invoice: Invoice;
    transactions: Transaction[];
    payments: ProviderClaimPayment[];
}

export const InvoiceDetail: React.FunctionComponent<IInvoiceDetailProps> = inject('rootStore')(
    observer(
        (props: IInvoiceDetailProps): React.ReactElement => {
            const dialogContext = useContext(DialogContext);

            const rootStore: RootStore = _.get(props, 'rootStore');
            if (!rootStore) {
                return <></>;
            }
            const { claimStore } = rootStore;
            const { invoice, transactions, payments } = props;

            const items = _.flatten(_.map(payments, payment => payment.items));
            const codeItemMap = _.reduce(
                _.compact(items),
                (acc: Record<string, ProviderClaimPaymentItem>, item: ProviderClaimPaymentItem) => {
                    if (!item?.procedureCode) {
                        return acc;
                    }

                    acc[item?.procedureCode] = item;

                    return acc;
                },
                {},
            );

            const [paymentPlans, setPaymentPlans] = useState<ProviderPaymentPlan[]>([]);

            const updatePlans = useCallback(async (): Promise<void> => {
                if (invoice && invoice.id) {
                    const plans = await claimStore.findPaymentPlansByInvoiceId(invoice.id);
                    if (plans && plans.length) {
                        setPaymentPlans(plans);
                    } else {
                        setPaymentPlans([]);
                    }
                }
            }, [claimStore, invoice, setPaymentPlans]);

            const handleCreatePlan = (): void => {
                const handleClose = () => {
                    dialogContext.hideDialog();
                };

                const handleSubmit = (plan: ProviderPaymentPlan) => {
                    handleClose();
                    setPaymentPlans([...paymentPlans, plan]);
                };

                dialogContext.showDialog({
                    props: {
                        onClose: handleClose,
                        disableBackdropClick: true,
                        disableEscapeKeyDown: true,
                        renderTitle: 'Create Payment Plan',
                    },
                    Component: (
                        <CreatePlanDialog onClose={handleClose} onSubmit={handleSubmit} invoiceId={invoice.id} />
                    ),
                });
            };

            useEffect(() => {
                updatePlans();
            }, [updatePlans]);

            // remove `ENTER_ADJUSTMENT` items from the list
            const invoiceItems = (invoice.items || []).filter(item => item.itemId !== 'ENTER_ADJUSTMENT');

            const itemRows = _.map(invoiceItems, (item, i) => {
                const paymentItem = item.itemId ? codeItemMap[item.itemId] : undefined;
                const fromDos = paymentItem?.fromDos ? renderDate(paymentItem.fromDos) : undefined;
                const throughDos = paymentItem?.throughDos ? renderDate(paymentItem.throughDos) : undefined;

                const dates = _.compact([fromDos, throughDos]).join(' - ');

                return (
                    <React.Fragment key={item.id}>
                        <S.TR>
                            <S.TD colSpan={2}>
                                {item.quantity} {item.itemId} {item.name}
                            </S.TD>
                            <S.TD>{!!dates && dates}</S.TD>
                            <S.TD align="right">{renderAmount(item.price)}</S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TD colSpan={2}>{item.description}</S.TD>
                            <S.TD />
                        </S.TR>
                        {item.discount && (
                            <S.TR>
                                <S.TD />
                                <S.TD>Discount</S.TD>
                                <S.TD align="right">{renderAmount(item.discount)}</S.TD>
                            </S.TR>
                        )}
                        {item.tax && (
                            <S.TR>
                                <S.TD />
                                <S.TD>Tax</S.TD>
                                <S.TD align="right">{renderAmount(item.tax)}</S.TD>
                            </S.TR>
                        )}
                    </React.Fragment>
                );
            });

            const transactionRows = _.map(transactions, transaction => (
                <S.TR key={transaction.id}>
                    <S.TD>{renderDate(transaction.created)}</S.TD>
                    <S.TD align="right">{renderAmount(transaction.amount)}</S.TD>
                    <S.TD>{transaction.refund}</S.TD>
                    <S.TD>{transaction.billedByDescriptor}</S.TD>
                    <S.TD>{transaction.status}</S.TD>
                    <S.TD>{transaction.memo}</S.TD>
                </S.TR>
            ));
            const renderPaymentPlans = (plans: ProviderPaymentPlan[]) => {
                return plans.map(plan => {
                    const { amountPerCycle, intervalCount, intervalSpan } = plan;
                    const interval = intervalCount !== 1 ? intervalCount : '';
                    const suffix = intervalCount !== 1 ? 's' : '';
                    return (
                        <div key={plan.id!}>
                            ${_.get(amountPerCycle, 'amount')} payment every {interval} {_.toLower(intervalSpan)}
                            {suffix}
                        </div>
                    );
                });
            };
            const renderInvoice = (): React.ReactElement => (
                <S.Table expand={true}>
                    <S.TBody key={invoice.id}>
                        <S.TR>
                            <S.TH>Invoice Date</S.TH>
                            <S.TD>{renderDate(invoice.invoiceDate)}</S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Invoice Type</S.TH>
                            <S.TD>{invoice.invoiceType}</S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Invoice Status</S.TH>
                            <S.TD>{invoice.invoiceStatus} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Due Date</S.TH>
                            <S.TD>{renderDate(invoice.dueDate)} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Amount</S.TH>
                            <S.TD>{renderAmount(invoice.amount)} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH> Paid Amount</S.TH>
                            <S.TD>{renderAmount(invoice.paidAmount)} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH> Remaining Amount</S.TH>
                            <S.TD>{renderAmount(invoice.remainingAmountDue)} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH> Memo</S.TH>
                            <S.TD>{invoice.memo} </S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Description</S.TH>
                            <S.TD>{invoice.description}</S.TD>
                        </S.TR>
                        <S.TR>
                            <S.TH>Payment Plans</S.TH>
                            <S.TD>{paymentPlans.length ? renderPaymentPlans(paymentPlans) : '--'}</S.TD>
                        </S.TR>
                    </S.TBody>
                </S.Table>
            );
            const renderItems = (): React.ReactElement => (
                <S.Table expand={true}>
                    <S.THead>
                        <tr>
                            <S.TH colSpan={2}>Invoice Items</S.TH>
                        </tr>
                    </S.THead>
                    <S.TBody>
                        {itemRows}
                        {_.isEmpty(itemRows) && (
                            <S.TR>
                                <S.TD colSpan={3}>No items recorded</S.TD>
                            </S.TR>
                        )}
                    </S.TBody>
                </S.Table>
            );
            const renderTransactions = (): React.ReactElement => (
                <S.Table expand={true}>
                    <S.THead>
                        <tr>
                            <S.TH colSpan={2}>Transactions</S.TH>
                        </tr>
                    </S.THead>
                    {transactions && (
                        <S.TBody>
                            <S.TR>
                                <S.TH>Date</S.TH>
                                <S.TH align="right">Amount</S.TH>
                                <S.TH>Refund</S.TH>
                                <S.TH>Billed by</S.TH>
                                <S.TH>Status</S.TH>
                                <S.TH>Memo</S.TH>
                            </S.TR>
                            {transactionRows}
                            {!transactions.length && (
                                <S.TR>
                                    <S.TD colSpan={2}>No transactions recorded</S.TD>
                                </S.TR>
                            )}
                        </S.TBody>
                    )}
                </S.Table>
            );
            return (
                <div>
                    {renderInvoice()}
                    {renderItems()}
                    {renderTransactions()}
                    <S.StyledButton variant="primary" onClick={handleCreatePlan}>
                        <S.StyledLightIcon path={mdiPlus} size={1} /> Create Payment Plan
                    </S.StyledButton>
                </div>
            );
        },
    ),
);
